// ADR 15.9.26ag §4 — the page's own bar: a way back, a title, a trailing slot.
//
// Android's `TopAppBar`. No fill and no border, so it sits on whatever the
// page is; `min-h-14` is Material's bar height. The leading button renders
// only when `to` is given — a page with nowhere to go back to has no arrow,
// rather than a disabled one — and it is an `IconButton`, so it carries the
// ring and the 44px target without this file saying so. `useNavigate` rather
// than a `Link` dressed as a button: the thing is a button (it is pressed, it
// does not open in a new tab), and `ListRow` already brings the router into
// `ui/`, so this is not a new dependency edge.

import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { IconButton } from './controls'
import type { HeadingLevel } from './SectionHeading'

export interface TopBarProps {
  title: ReactNode
  /**
   * ADR 15.9.26ai §6 — where the title sits in the outline. `1` is the app
   * header's (15.9.26ah §5, the page's one `<h1>`); a page's own bar sits
   * under it at `2`. A bar is never an `<h3>`: that is a `SectionHeading`
   * in a flex row. The look does not move with the level — `SectionHeading`'s
   * own `level` / `size` split, applied to the bar.
   */
  level?: Extract<HeadingLevel, 1 | 2>
  /** Where the leading arrow goes. Absent = no arrow. */
  to?: string
  /** The arrow's accessible name; required with `to`. Callers pass a translated string. */
  backLabel?: string
  /**
   * ADR 15.9.26ah §5 — what sits in the leading position when there is no
   * arrow: the app header's brand mark. Ignored when `to` is given, so the
   * default rendering (arrow with `to`, nothing without) is unchanged.
   */
  leading?: ReactNode
  /** The trailing slot: actions, an overflow menu. */
  children?: ReactNode
  className?: string
}

export function TopBar({
  title,
  level = 1,
  to,
  backLabel = '',
  leading,
  children,
  className = '',
}: TopBarProps) {
  const navigate = useNavigate()
  const Title = level === 1 ? 'h1' : 'h2'
  return (
    <header className={`flex min-h-14 items-center gap-2${className === '' ? '' : ` ${className}`}`}>
      {to === undefined ? (
        leading ?? null
      ) : (
        <IconButton icon="arrow_back" label={backLabel} onClick={() => navigate(to)} />
      )}
      <Title className="min-w-0 flex-1 truncate text-title-lg">{title}</Title>
      {children === undefined ? null : <div className="flex items-center gap-1">{children}</div>}
    </header>
  )
}
